import React from 'react';
import Head from 'next/head';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import Icon from '../components/icon/Icon';
import Link from 'next/link';
import PostBody from '../components/post-body';
import PostPreview from '../components/post-preview';
import config from '../lib/config';

import { GetStaticProps } from 'next';
import { H2, H3, Muted, P } from '../components/text';
import { fetchAPI, getPrimaryMenuItems } from '../lib/api';
import { ISponsor } from '../models/sponsor';
import { IMenuItem } from '../models/menu-item';
import { IPost, IYoastMeta } from '../models/post';
import { IFeaturedImage } from '../models/featured-image';

interface IProps {
	sponsors: ISponsor[];
	primaryMenuItems: IMenuItem[];
	page: {
		title: string;
		content: string | TrustedHTML;
		featuredImage: IFeaturedImage;
		newsCardGroup: INewsCardGroup;
		mvoLogos: IMvoLogos;
		seo: IYoastMeta;
	};
	categoryName: string;
	posts: IPost[];
}

export interface INewsCardGroup {
	newsCards: INewsCard[];
}

export interface INewsCard {
	newsCardImage: INewsCardImage;
	newsCardTitle: string;
	newsCardExcerpt: string;
	newsCardLink: INewsCardLink;
}

export interface INewsCardImage {
	sourceUrl: string;
	altText: string;
}
export interface INewsCardLink {
	url: string;
	title: string;
	target: string;
}

export interface IMvoLogos {
	mvoLogos: IMvoLogo[];
}

export interface IMvoLogo {
	mvoLabel: string;
	mvoUrl: string;
	mvoImage: IMvoImage;
}

export interface IMvoImage {
	altText: string;
	sourceUrl: string;
}

export default function MvoPage({ page, posts, primaryMenuItems, sponsors }: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<div className="bg-red-500 sm:py-4 lg:p-0 lg:bg-transparent relative overflow-hidden">
				<Container noPaddingMobile>
					<div className="flex flex-col lg:flex-row">
						<div className="flex-1 order-2 lg:order-1 z-10">
							<div className="p-5 lg:p-0 h-full flex justify-center flex-col">
								<H2 className="text-white mb-4 uppercase">Voetbal geeft</H2>
								<P className="text-white">
									De VriendenLoterij zet zich samen met de VriendenLoterij Eredivisie en Keuken
									Kampioen Divisie dagelijks in om een positieve bijdrage te leveren aan de
									samenleving. Door te werken aan een gezondere, inclusievere en sterkere maatschappij
									laat het voetbal zien wat het kan betekenen buiten de lijnen. In de videoserie
									Voetbal Geeft wordt ingezoomd op de maatschappelijke impact van clubs en op wat die
									inzet kan betekenen voor één individu.
								</P>
							</div>
						</div>
						<div className="flex-1 order-1 lg:order-2 z-10">
							{page.featuredImage && (
								<div className="w-full">
									<img
										src={page.featuredImage.node.sourceUrl}
										alt={page.featuredImage.node.altText}
										className="lg:scale-[.8] sm:rounded"
									/>
								</div>
							)}
						</div>
					</div>
				</Container>
				<div className="hidden lg:block bg-red-500 absolute bottom-0 left-0 right-0 h-full lg:h-[620px] lg:w-full xl:w-[1200px]"></div>
			</div>

			<Container className="py-12 xl:py-16">
				<div className="flex flex-col lg:flex-row">
					<div
						className="w-full xl:w-4/12
						 bg-white p-6 xl:p-12 mb-12 h-full rounded"
					>
						<H3 className="mb-4">{page.title}</H3>
						<section>{page.content && <PostBody content={page.content} />}</section>
					</div>
					<div className="flex-1 lg:pl-6 xl:pl-16 w-full xl:w-8/12">
						<section>
							<H2 className="mb-4">Thema&apos;s</H2>
							<P className="mb-12">
								In 2024/2025 omarmden de clubs het gedachtegoed van Positieve Gezondheid: fysiek,
								sociaal én mentaal welbevinden. De Keuken Kampioen Divisie en VriendenLoterij Eredivisie
								zetten die kracht dagelijks in om Nederlanders met elkaar te verbinden en gezonder te
								maken. Gezondheid vormt het hart van de maatschappelijke onze gezamenlijke koers. De
								urgentie is groot: de helft van alle volwassenen heeft overgewicht, één op de drie
								jongeren kampt met mentale klachten en 40% van de mensen voelt zich regelmatig eenzaam.
								Met ons landelijk platform, regionale verankering, rolmodellen en herkenbare identiteit
								bereiken de 34 clubs juist díe mensen die beleid en zorg vaak niet bereiken.
							</P>
						</section>
						<section>
							{page.newsCardGroup.newsCards.map((item, i) => (
								<article
									key={i}
									className="relative isolate flex flex-col sm:gap-8 lg:flex-row bg-white rounded overflow-hidden mb-4"
								>
									<div className="relative aspect-[16/9] sm:aspect-[2/1] lg:aspect-square lg:w-64 lg:shrink-0">
										<img
											src={item.newsCardImage.sourceUrl}
											alt={item.newsCardImage.altText}
											className="absolute inset-0 h-full w-full  bg-gray-50 object-cover"
										/>
									</div>
									<div>
										<div className="group relative max-w-5xl p-4 sm:p-8 sm:-ml-4">
											<H3 className="py-2">{item.newsCardTitle}</H3>
											<P className="mb-4 line-clamp-3">{item.newsCardExcerpt}</P>

											<Link href={item.newsCardLink.url} className="flex items-center">
												<span className="inline-block mr-3 text-red-500 hover:text-red-700 font-inter font-semibold uppercase tracking-widest text-base lg:text-lg hover:underline">
													Lees meer
												</span>
												<Icon icon={'arrow-right'} color="#E2013D" size={18} />
											</Link>
										</div>
									</div>
								</article>
							))}
						</section>
					</div>
				</div>

				<div>
					<section className="mb-12">
						<H2 className="py-6">Laatste nieuws</H2>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							{posts.map((post) => (
								<PostPreview
									key={post.node.slug}
									title={post.node.title}
									coverImage={post.node.featuredImage}
									date={post.node.date}
									slug={post.node.slug}
									badge={post.node?.tags?.nodes[0]?.name}
									excerpt={post.node.excerpt}
									bgColor="solid"
								/>
							))}
						</div>
					</section>

					<section className="mb-12">
						<H3 className="py-6">Maatschappelijke pagina&apos;s clubs</H3>
						<div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4">
							{page.mvoLogos.mvoLogos.map((item, i) => (
								<Link
									key={i}
									href={item.mvoUrl}
									target="_blank"
									className="bg-white p-3 h-32 max-w-48 md:h-48 rounded shadow-md flex justify-center flex-col items-center cursor-pointer hover:shadow-lg text-center"
								>
									<img
										className="w-24 h-24 lg:w-32 lg:h-32 object-contain mb-3"
										src={item.mvoImage.sourceUrl}
										alt={item.mvoImage.altText}
									/>
									<Muted className="hidden md:block">{item.mvoLabel}</Muted>
								</Link>
							))}
						</div>
					</section>
				</div>
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();

	const categoryId = '60';
	const data = await fetchAPI<{ page: any; category: any; sponsors: any }>(
		`
		query MvoPageQuery($categoryId: ID!) {
			page(id: 112220, idType: DATABASE_ID) {
				title
				content
				featuredImage {
					node {
						altText
						sourceUrl
					}
				}
				mvoLogos {
					mvoLogos {
						mvoLabel
						mvoUrl
						mvoImage {
							altText
							sourceUrl
						}
					}
				}
				newsCardGroup {
					newsCards {
						newsCardImage {
							sourceUrl
							altText
						}
						newsCardTitle
						newsCardExcerpt
						newsCardLink {
							url
							title
							target
						}
					}
				}
			}
			category(id: $categoryId, idType: DATABASE_ID) {
				name
				posts (first: 6) {
					edges {
						node {
							id
							title
							slug
							date
							excerpt
							featuredImage {
								node {
									sourceUrl
								}
							}
							tags(first: 1) {
								nodes {
									name
								}
							}
							author {
								node {
									name
									firstName
									lastName
									avatar {
										url
									}
								}
							}
						}
					}
				}
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
		{ variables: { categoryId } },
	);

	const { sponsors, category, page } = data;

	return {
		props: {
			posts: category.posts.edges,
			page,
			categoryName: category.name,
			sponsors: sponsors.nodes,
			preview,
			primaryMenuItems,
		},
		revalidate: 3600,
	};
};
